import Button from '@/components/Button';
import { HiOutlineSparkles, IoDocumentOutline, RiRobot2Line } from '@/components/react-icons/main';
import WizardToggleButton from '@/components/wizard/WizardToggleButton';
import { AddIcon } from '@/lib/constants/icons.js';
import { postAiAction } from '@/redux/actions/gravityActions.js';
import { setModalState } from '@/redux/actions/uiActions/modalActions.js';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';
import selectors from '@/redux/selectors/selectors.js';
import AppScreenPublishActions from '@/screens/apps/appEditorPage/appScreenActions/AppScreenPublishActions';
import { isPublishableApp } from '@shared/utils/isPublishableApp.js';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import appActions from '../redux/actions/appActions.js';
import appScreenSelectors from '../redux/selectors/appScreenSelectors.js';
import AgentConnectionModal from './AgentConnectionModal';
import AuthenticateButton from './appEditorMainPanel/authenticationPage/AuthenticateButton';

const AppScreenActions = () => {
  const section = useArgSelector(selectors.param, 'section');
  const arg1 = useArgSelector(selectors.param, 'arg1');
  const appSlug = useSelector((state) => appScreenSelectors.activeApp(state)?.slug);
  const appName = useSelector((state) => appScreenSelectors.activeApp(state)?.name);
  const authDocumentationUrl = useSelector((state) => appScreenSelectors.activeAppAuth(state)?.documentation?.url);
  const authId = useSelector((state) => appScreenSelectors.activeAppAuth(state)?.id);
  const authType = useSelector((state) => appScreenSelectors.activeAppAuth(state)?.type);
  const apiType = useSelector((state) => appScreenSelectors.activeAppApi(state)?.type);
  const isPublishable = isPublishableApp(appSlug);
  const agentConnection = useSelector((state) => appScreenSelectors.activeApp(state)?.agentConnection);
  const [agentConnectionModalOpen, setAgentConnectionModalOpen] = useState(false);

  const handleAddActionClick = () => {
    setModalState('createAction', { open: true });
  };

  const handleImportSettings = () => {
    setModalState('parseImportSettingsModal', { open: true });
  };

  const handleAddAuthClick = () => {
    setModalState('auth', { open: true, mode: 'add' });
  };

  const handleAddApiClick = () => {
    setModalState('api', { open: true, mode: 'add' });
  };

  const handleAutofillClick = async () => {
    const response = await postAiAction({
      action: 'appOAuth2',
      context: {
        company: appName
      }
    });
    await appActions.set(`authentication[id:${authId}]`, (authState) => ({
      ...authState,
      testedOn: null,
      documentationUrl: response?.documentation?.url,
      scopes: response?.scopes,
      authorizeRequest: {
        ...authState.authorizeRequest,
        url: response?.authorizeUrl
      },
      accessTokenRequest: {
        ...authState.accessTokenRequest,
        method: response?.accessTokenMethod,
        url: response.accessTokenUrl
      }
    }));
  };

  const handleApiAutofillClick = async () => {
    const response = await postAiAction({
      action: 'findRestApiBaseUrl',
      context: { company: appName }
    });
    appActions.set(`api[id:${arg1}]`, (apiState) => ({
      ...apiState,
      baseUrl: response?.baseUrl
    }));
  };

  const hasExistingAgentConnection = agentConnection?.credentialId && agentConnection?.description?.trim?.()?.length > 0;

  const handleAgentConnectionClick = () => {
    setAgentConnectionModalOpen(true);
  };

  return (
    <>
      {isPublishable && <AppScreenPublishActions />}
      {section === 'actions' && !arg1 && (
        <>
          <Button size="sm" onClick={handleImportSettings}>
            Import Settings
          </Button>
          <Button size="sm" icon={AddIcon} onClick={handleAddActionClick}>
            Add Action
          </Button>
        </>
      )}
      {section === 'actions' && arg1 && (
        <Button size="sm" onClick={handleImportSettings}>
          Import Settings
        </Button>
      )}
      {section === 'auth' && !arg1 && (
        <>
          <Button size="sm" icon={AddIcon} onClick={handleAddAuthClick}>
            Add Auth
          </Button>
        </>
      )}
      {section === 'auth' && arg1 && (
        <>
          {authDocumentationUrl && (
            <Button size={'sm'} icon={IoDocumentOutline} route={authDocumentationUrl}>
              Docs
            </Button>
          )}
          {authType === 'oAuth2' && (
            <Button size={'sm'} icon={HiOutlineSparkles} onClick={handleAutofillClick}>
              Autofill
            </Button>
          )}
          {authType === 'oAuth2' && <AuthenticateButton size={'sm'} />}
        </>
      )}
      {section === 'api' && !arg1 && (
        <>
          <Button size={'sm'} icon={AddIcon} onClick={handleAddApiClick}>
            Add API
          </Button>
        </>
      )}
      {section === 'api' && arg1 && apiType === 'rest' && (
        <Button size={'sm'} icon={HiOutlineSparkles} onClick={handleApiAutofillClick}>
          Autofill
        </Button>
      )}
      <Button size="sm" icon={RiRobot2Line} iconProps={hasExistingAgentConnection ? { className: 'valid' } : undefined} onClick={handleAgentConnectionClick} title="Agent Connection">
        Agent Connection
      </Button>
      <WizardToggleButton />
      <AgentConnectionModal show={agentConnectionModalOpen} onHide={() => setAgentConnectionModalOpen(false)} initialCredentialId={hasExistingAgentConnection ? agentConnection?.credentialId : undefined} initialCompanyId={hasExistingAgentConnection ? agentConnection?.companyId : undefined} initialDescription={hasExistingAgentConnection ? agentConnection?.description : undefined} initialBlockCreates={hasExistingAgentConnection ? agentConnection?.blockCreates : undefined} initialBlockUpdates={hasExistingAgentConnection ? agentConnection?.blockUpdates : undefined} initialBlockDeletes={hasExistingAgentConnection ? agentConnection?.blockDeletes : undefined} />
    </>
  );
};

export default AppScreenActions;
